import { useState, useEffect } from "react";
import { useSignIn, useAuthUser, useIsAuthenticated } from "react-auth-kit";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "@/components/styles/login.css";
import { api } from "@/helper/api";
import { useDispatch } from "react-redux";
import { setLogin } from "@/components/state/parent/loginSlice";
import { useForm } from "react-hook-form";
import PasswordInput from "@/components/forms/inputs/passwordInput";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { sanitizeLoginReturnPath } from "@/helper/sanitizeLoginReturnPath";
import { setManagerLmsAccessReady } from "@/components/state/lms/managerLmsAccessSlice";
import { normalizeLmsStartVertragSelection } from "@/components/lms/lmsQuickSearchEligibility";

interface Props {
  errorHandler: (errorCode: string) => void;
  errorReset: () => void;
  portalName: () => any;
  subdomain?: string;
  token?: string | null;
}

interface LocationStateWithFrom {
  from?: {
    pathname?: string;
    search?: string;
    hash?: string;
  };
}

const RETURN_TO_SESSION_STORAGE_KEY = "post_login_return_to";
const MANUAL_LOGOUT_SESSION_STORAGE_KEY = "manual_logout";
const MANUAL_LOGOUT_PATH_SESSION_STORAGE_KEY = "manual_logout_path";

export default function LoginForm(props: Props) {
  const [loginstate, setLoginstate] = useState<number>(0);
  const [isFormHidden, setIsFormHidden] = useState<boolean>(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const signIn = useSignIn();

  const getAuth = useAuthUser();
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useDispatch();
  const { showConfirmationModalAsync } = useConfirmationModal();
  const locationState = location.state as LocationStateWithFrom | null;
  const isManualLogout =
    sessionStorage.getItem(MANUAL_LOGOUT_SESSION_STORAGE_KEY) === "true";
  const manualLogoutPath = sanitizeLoginReturnPath(
    sessionStorage.getItem(MANUAL_LOGOUT_PATH_SESSION_STORAGE_KEY)
  );
  const fromPath = locationState?.from
    ? [
        locationState.from.pathname ?? "",
        locationState.from.search ?? "",
        locationState.from.hash ?? "",
      ].join("")
    : null;
  const stateReturnTo = sanitizeLoginReturnPath(fromPath);
  const queryReturnTo = sanitizeLoginReturnPath(searchParams.get("returnTo"));
  const isManualLogoutReturnPath =
    !!isManualLogout &&
    !!stateReturnTo &&
    !!manualLogoutPath &&
    stateReturnTo === manualLogoutPath;
  const shouldSuppressReturnRestore =
    isManualLogout && (!stateReturnTo || isManualLogoutReturnPath);

  const resolveReturnPath = () => {
    if (shouldSuppressReturnRestore) {
      return "/";
    }
    const storedReturnTo = sanitizeLoginReturnPath(
      sessionStorage.getItem(RETURN_TO_SESSION_STORAGE_KEY)
    );
    return stateReturnTo || queryReturnTo || storedReturnTo || "/";
  };

  const navigateToPostLoginTarget = () => {
    const returnPath = resolveReturnPath();
    sessionStorage.removeItem(RETURN_TO_SESSION_STORAGE_KEY);
    sessionStorage.removeItem(MANUAL_LOGOUT_SESSION_STORAGE_KEY);
    sessionStorage.removeItem(MANUAL_LOGOUT_PATH_SESSION_STORAGE_KEY);
    navigate(returnPath, { replace: true });
  };

  const { register, handleSubmit, control } = useForm({
    mode: "onChange",
    defaultValues: {
      userName: "",
      userPassword: "",
    },
  });

  const handleUserLogin = (data: any) => {
    const sessionExpiration = Math.floor(data.token_expiry_in / 60);
    if (
      signIn({
        token: data.email,
        expiresIn: sessionExpiration,
        tokenType: "Session",
        authState: {
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          company_name: data.company_name,
        },
      })
    ) {
      setLoginstate(1);

      dispatch(
        setLogin({
          subjectId: data.subject_id,
          firstname: data.first_name,
          lastname: data.last_name,
          email: data.email,
          companyName: data.company_name,
          permissions: data.permissions,
          company_enforced_two_factor: data?.company_enforced_two_factor,
          group_set: data.group_set,
          preferred_language: data.person.preferred_language,
          userId: data.user_id,
          assigned_companies: data?.assigned_companies,
          department_manager_for: data?.department_manager_for,
          hasCourseEnrolments: data?.has_course_enrolments === true,
        })
      );
      if (props.subdomain === "manage" || props.subdomain === "portal") {
        dispatch(
          setManagerLmsAccessReady({
            subjectId: String(data.subject_id ?? ""),
            selection: normalizeLmsStartVertragSelection(data),
            hasCourseEnrolments:
              props.subdomain === "portal"
                ? data?.has_course_enrolments === true
                : null,
          })
        );
      }
      if (props.subdomain === "portal" && data.group_set.includes("manager")) {
        openNavigateToManagePortal();
      }
    } else {
      console.log("login was not successful");
    }
  };

  const openNavigateToManagePortal = async () => {
    const confirm = await showConfirmationModalAsync(
      t("confirm_navigate_to_managerportal"),
      {
        confirmButtonText: t("button_managerportal"),
        cancelButtonText: t("button_customerportal"),
      }
    );
    if (!confirm) {
      return;
    }
    window.open(
      "https://manage.health-kaer.de",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleVerfify2FA = async (token: string) => {
    const confirmLogin = await showConfirmationModalAsync(
      t("2fa_confirm_login")
    );
    if (!confirmLogin) {
      setIsFormHidden(false);
      return;
    }

    const additionalUrlParameters: any = {};
    additionalUrlParameters["token"] = token;

    try {
      const res: any = await new Promise((resolve, reject) => {
        api.genericApiRequest({
          method: "get",
          entity: "verify-2fa",
          parametersToRender: additionalUrlParameters,
          successHandler: (res: any) => {
            resolve(res);
          },
          failHandler: (error: any) => {
            reject(error);
          },
        });
      });
      handleUserLogin(res.data);
    } catch {
      props.errorHandler("invalid");
    }
  };
  useEffect(() => {
    if (props.token) {
      setIsFormHidden(true);
      handleVerfify2FA(props.token);
    }
  }, [props.token]);

  useEffect(() => {
    if (shouldSuppressReturnRestore) {
      sessionStorage.removeItem(RETURN_TO_SESSION_STORAGE_KEY);
    }
  }, [shouldSuppressReturnRestore]);

  useEffect(() => {
    const returnTo = stateReturnTo || queryReturnTo;
    if (!shouldSuppressReturnRestore && returnTo) {
      sessionStorage.setItem(RETURN_TO_SESSION_STORAGE_KEY, returnTo);
      sessionStorage.removeItem(MANUAL_LOGOUT_SESSION_STORAGE_KEY);
      sessionStorage.removeItem(MANUAL_LOGOUT_PATH_SESSION_STORAGE_KEY);
    }
  }, [queryReturnTo, shouldSuppressReturnRestore, stateReturnTo]);

  useEffect(() => {
    if (isAuthenticated()) {
      const auth = getAuth();

      if (auth && auth.email) {
        setLoginstate(1);
      }
    }
  }, [getAuth, isAuthenticated]);

  useEffect(() => {
    if (loginstate === 1) {
      navigateToPostLoginTarget();
    }
  }, [loginstate]);

  const onSubmit = async (data: any) => {
    dispatch({ type: "clear" });

    const result = {
      username: data.userName,
      password: data.userPassword,
      // use_2fa: true,
    };

    try {
      const res: any = await new Promise<void>((resolve, reject) => {
        api.genericApiRequest({
          entity: "login",
          method: "post",
          submitData: result,
          successHandler: (res: any) => {
            resolve(res);
          },
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          failHandler: (error: any, status: any, code: any) => {
            reject(error);
            let tempError = "check_password_and_try_again";
            if (status === 401) tempError = "login_error_401";
            if (status === 429) tempError = "login_error_429";

            props.errorHandler(tempError);
          },
        });
      });
      if (res.status === 202) {
        const returnPath = resolveReturnPath();
        const stepPath =
          returnPath === "/"
            ? "/login/2fa"
            : `/login/2fa?returnTo=${encodeURIComponent(returnPath)}`;
        navigate(stepPath, { replace: true, state: location.state });
      }
      if (res.data) {
        handleUserLogin(res.data);
      }
    } catch {
      //left blank in this case because of failhandler and not using the error itself
    }
  };

  if (loginstate === 0 && !isFormHidden) {
    return (
      <>
        <form
          className="login_form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <h1 className="heading textAlignCenter">{props.portalName()}</h1>

          <label htmlFor="login_email" className="login_formLabel">
            {t("login_email")}
          </label>
          <input
            {...register("userName")}
            className="login_formInput"
            onFocus={props.errorReset}
            id={"login_email"}
            data-testid={"login-email-input"}
          />
          <PasswordInput
            control={control}
            name={"userPassword"}
            id={t("password")}
            label={t("password")}
            title={t("password")}
            dynamicLabel={false}
          />

          <div className="login_passwordReminderWrapper">
            <a className="login_passwordReminderLink" href="/login/reminder/">
              {t("password_forgotten")}
            </a>
            <GenericButton
              className="login_submitButton"
              data-testid={"login-button"}
              type="submit"
            >
              {t("login")}
            </GenericButton>
          </div>
        </form>
      </>
    );
  } else {
    return <></>;
  }
}
//TODO CUSTOMERROR
